import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Search, Globe, Mail, Phone, MapPin, ArrowLeft, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExhibitorDirectory = () => {
  const [exhibitors, setExhibitors] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [selectedSector, setSelectedSector] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([axios.get(`${API}/exhibitors`), axios.get(`${API}/sectors`)])
      .then(([exRes, secRes]) => { setExhibitors(exRes.data); setSectors(secRes.data); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = exhibitors.filter(ex => {
    const matchSector = selectedSector === 'all' || ex.sector === selectedSector;
    const matchSearch = !searchQuery || ex.company_name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchSector && matchSearch;
  });

  return (
    <div className="min-h-screen pt-16">
      {/* PAGE HERO */}
      <section className="relative py-24 lg:py-32 bg-[#1E3A5F] overflow-hidden" data-testid="directory-hero">
        <div className="absolute inset-0 opacity-10" aria-hidden="true" />
        <div className="relative container-institutional">
          <p className="text-sm font-semibold uppercase tracking-widest text-[#F5A623] mb-4">Exposition</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">Répertoire des Exposants</h1>
          <p className="text-lg text-white/70 max-w-2xl leading-relaxed">Découvrez les entreprises qui exposent leurs innovations au Forum URBANIK 2026.</p>
          {/* Search + Filter */}
          <div className="flex flex-col sm:flex-row gap-4 max-w-2xl mt-10">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <input type="text" placeholder="Rechercher un exposant..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-11 pr-4 py-3.5 text-sm bg-white/10 border border-white/20 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:bg-white/15 focus:border-white/40" data-testid="search-input" />
            </div>
            <select value={selectedSector} onChange={(e) => setSelectedSector(e.target.value)} className="px-4 py-3.5 text-sm bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none sm:w-52 appearance-none" data-testid="sector-filter">
              <option value="all" className="text-[#1E3A5F]">Tous les secteurs</option>
              {sectors.map(s => <option key={s} value={s} className="text-[#1E3A5F] capitalize">{s}</option>)}
            </select>
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="py-20 lg:py-28" data-testid="exhibitor-grid">
        <div className="container-institutional">
          {loading ? <p className="text-center text-[#94A3B8] py-12">Chargement...</p> : filtered.length === 0 ? (
            <p className="text-center text-[#94A3B8] py-12">Aucun exposant trouvé.</p>
          ) : (
            <>
              <p className="text-sm text-[#94A3B8] mb-8">{filtered.length} exposant{filtered.length > 1 ? 's' : ''}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map(ex => (
                  <Link key={ex.id} to={`/exposition/repertoire/${ex.id}`} className="bg-white border border-[#E1E8F0] rounded-lg p-6 hover:shadow-md transition-shadow group" data-testid={`exhibitor-card-${ex.id}`}>
                    <div className="flex items-center gap-4 mb-4">
                      <img src={ex.logo_url || 'https://placehold.co/80x40/E1E8F0/6B7C93?text=Logo'} alt={ex.company_name} className="h-10 w-auto" />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-[#1E3A5F] truncate group-hover:text-[#F5A623] transition-colors">{ex.company_name}</h3>
                        <p className="text-xs text-[#94A3B8] capitalize">{ex.sector}</p>
                      </div>
                    </div>
                    {ex.description && <p className="text-sm text-[#6B7C93] line-clamp-2 mb-3">{ex.description}</p>}
                    {ex.stand && (
                      <div className="flex items-center gap-2 text-xs bg-[#F7F9FC] rounded px-3 py-1.5"><MapPin className="w-3 h-3 text-[#F5A623]" /><span className="text-[#4A5568]">Stand {ex.stand.stand_number} – Zone {ex.stand.zone}</span></div>
                    )}
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#1E3A5F]" data-testid="directory-cta">
        <div className="container-institutional text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Devenez Exposant</h2>
          <p className="text-white/70 max-w-xl mx-auto mb-8">Rejoignez les entreprises qui font l'innovation du BTP.</p>
          <Link to="/exposition/devenir-exposant" className="btn-primary text-base px-8 py-4">Devenir Exposant <ArrowRight className="w-5 h-5 ml-2" /></Link>
        </div>
      </section>
    </div>
  );
};

const ExhibitorProfile = () => {
  const { id } = useParams();
  const [exhibitor, setExhibitor] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/exhibitors/${id}`).then(r => setExhibitor(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="min-h-screen pt-16 flex items-center justify-center"><p className="text-[#94A3B8]">Chargement...</p></div>;
  if (!exhibitor) return <div className="min-h-screen pt-16 flex items-center justify-center"><p className="text-[#94A3B8]">Exposant non trouvé.</p></div>;

  return (
    <div className="min-h-screen pt-16">
      <section className="py-20 lg:py-28 bg-[#F7F9FC]" data-testid="exhibitor-profile">
        <div className="container-institutional">
          <Link to="/exposition/repertoire" className="inline-flex items-center gap-2 text-sm text-[#6B7C93] hover:text-[#1E3A5F] mb-8 transition-colors"><ArrowLeft className="w-4 h-4" /> Retour au répertoire</Link>
          <div className="bg-white border border-[#E1E8F0] rounded-lg p-8 lg:p-12">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-shrink-0"><img src={exhibitor.logo_url || 'https://placehold.co/200x100/E1E8F0/6B7C93?text=Logo'} alt={exhibitor.company_name} className="h-20 w-auto" /></div>
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-[#1E3A5F] mb-2" data-testid="exhibitor-name">{exhibitor.company_name}</h1>
                <p className="text-sm text-[#F5A623] font-semibold capitalize mb-6">{exhibitor.sector}</p>
                {exhibitor.description && <p className="text-[#4A5568] leading-relaxed mb-6">{exhibitor.description}</p>}
                {exhibitor.products_services && <div className="mb-6"><h3 className="text-sm font-bold text-[#1E3A5F] mb-2">Produits & Services</h3><p className="text-sm text-[#6B7C93] leading-relaxed">{exhibitor.products_services}</p></div>}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-6 border-t border-[#E1E8F0]">
                  {exhibitor.website && <a href={exhibitor.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-[#4A5568] hover:text-[#1E3A5F]"><Globe className="w-4 h-4 text-[#94A3B8]" />{exhibitor.website}</a>}
                  {exhibitor.email && <a href={`mailto:${exhibitor.email}`} className="flex items-center gap-2 text-sm text-[#4A5568] hover:text-[#1E3A5F]"><Mail className="w-4 h-4 text-[#94A3B8]" />{exhibitor.email}</a>}
                  {exhibitor.phone && <span className="flex items-center gap-2 text-sm text-[#4A5568]"><Phone className="w-4 h-4 text-[#94A3B8]" />{exhibitor.phone}</span>}
                  {exhibitor.stand && <span className="flex items-center gap-2 text-sm text-[#4A5568]"><MapPin className="w-4 h-4 text-[#F5A623]" />Stand {exhibitor.stand.stand_number} – Zone {exhibitor.stand.zone} ({exhibitor.stand.size})</span>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export { ExhibitorDirectory, ExhibitorProfile };
